import {
  GITHUB_INSTALL_REQUEST_NEXT,
  GITHUB_INSTALL_REQUEST_TITLE,
  githubInstallRequestBody,
} from "@/lib/githubInstallRequestCopy";

export const FIRST_RUN_COPY = {
  chrome: {
    logOut: "Log out",
    switchOrganization: "Switch organization",
    switchWorkspace: "Switch workspace",
    loggedInAs: "Logged in as",
    stepLabel: (step: number, total: number) => `Step ${step} of ${total}`,
    back: "Back",
  },
  welcome: {
    greeting: (firstName: string) => `Hi ${firstName}.`,
    headline: "See what SuperPlane can ship from your backlog",
    intro: "Each ticket is scored by how confident SuperPlane is that an agent can complete it.",
    getStarted: "Get started",
  },
  connect: {
    headline: "Connect GitHub",
    body: "SuperPlane reads your repositories. It does not start work yet.",
    signedInAs: (login: string) => `You are signed in to GitHub as ${login}.`,
    trust: "SuperPlane does not change code without your approval on a specific ticket.",
    connectGitHub: "Connect GitHub",
    selectAccount: "Select a GitHub account",
    selectAccountBody: "The SuperPlane GitHub App is already installed on these accounts.",
    useAccount: (account: string) => `Use ${account}`,
    missingAccount: "Do not see your GitHub account or organization?",
    installThere: "Install the GitHub App there.",
    connectError: "SuperPlane could not connect to GitHub. Check your access and try again.",
    installRequested: GITHUB_INSTALL_REQUEST_TITLE,
    installRequestedBody: githubInstallRequestBody,
    installRequestedNext: GITHUB_INSTALL_REQUEST_NEXT,
    openingGitHub: "Opening GitHub…",
    loadingAccounts: "Loading GitHub accounts…",
    connectingAccount: (account: string) => `Connecting ${account}…`,
  },
  choose: {
    headline: "Choose a repository",
    repositoryLabel: "Repository",
    repositoryHelper: "Select the repository you want SuperPlane to analyze.",
    searchPlaceholder: "Search repositories",
    missingRepository: "Do not see your repository?",
    editConnection: "Edit the GitHub connection.",
    accessHint: "You need admin access to the GitHub App or organization to change the repositories.",
    continue: "Choose a repository to continue",
    continueReady: "Continue",
    saving: "Saving repository…",
    loading: "Loading repositories…",
    moreLater: "You can add more repositories later.",
    missingTitle: "Why is my repository not shown?",
    missingReasons: [
      "The repository must be granted to the SuperPlane GitHub App.",
      "You need admin rights on the GitHub organization to add a repository.",
      "Editing the connection opens GitHub. A 404 there means you do not have access.",
    ],
  },
  tickets: {
    headline: "Connect your ticket system",
    trust: "SuperPlane does not change any tickets.",
    scoreHint:
      "It only analyzes them and shows a confidence score for how likely SuperPlane is to address each ticket.",
    githubIssues: "GitHub Issues",
    jira: "Jira",
    linear: "Linear",
    githubIssuesHelper: "Uses GitHub Issues on this repository. No extra setup.",
    jiraHelper: "Find tickets in your Jira backlog.",
    linearHelper: "Find tickets in your Linear backlog.",
    analyze: "Analyze my tickets",
    continue: "Connect agent",
    saving: "Saving ticket source…",
  },
  agent: {
    headline: "Connect agent",
    loading: "Checking agent availability…",
  },
  // Shared by every screen that provisions the workspace. Hosted credentials
  // move that action from the agent screen to the ticket screen.
  finish: {
    action: "Finish setup",
    saving: "Finishing setup…",
  },
  analysis: {
    headline: "Analyzing your backlog",
    body: "SuperPlane reads your code and your tickets. This takes a few minutes.",
    reassurance: "Nothing is changed. No work starts.",
    stage1: "Reading the repository structure",
    stage2: "Reading open tickets",
    stage3: "Scoring each ticket against the codebase",
    leaveHint: "You can leave this page. SuperPlane opens the board when the analysis finishes.",
    overrun: "The analysis needs more time than usual. It is still running.",
    failure: "The analysis did not finish. Try again.",
    retry: "Run analysis again",
    retrying: "Trying again…",
  },
  results: {
    headline: "Tickets SuperPlane can implement",
    subhead: "Each score shows how confident SuperPlane is that an agent can complete the ticket correctly.",
    approve: "Approve",
    approved: "Approved",
    helper: "Work starts only on tickets you approve.",
    empty: "No tickets scored above 65%. Connect more of your backlog or create a task yourself.",
    rescan: "Rescan backlog",
  },
  board: {
    backlogHintTitle: "Tickets land here first.",
    backlogHintBody:
      "New issues become tasks here. SuperPlane scores them for how well an agent can complete the work.",
  },
} as const;

export const FIRST_RUN_STAGES = [
  FIRST_RUN_COPY.analysis.stage1,
  FIRST_RUN_COPY.analysis.stage2,
  FIRST_RUN_COPY.analysis.stage3,
] as const;
